/**
 * Persistance : localStorage (mode hors ligne) ou coaches.app_data Supabase (sync cloud).
 * RLS : supabase/migrations/20260320_initial_schema.sql (+ charges) — pas de service_role côté client.
 */

import { create } from "zustand";
import { loadData, saveData, loadRemoteCoachState, mergeSettingsJsonIntoPayload } from "../services/storage";
import { isSupabaseConfigured, isSupabaseReady } from "../services/supabase";
import { supabasePersistence } from "../services/supabasePersistence";
import { pushNormalizedEntitiesFromStore } from "../services/pushNormalizedEntities";
import { seedDemoDataIfEmpty } from "../services/seedDemoData";
import { clientDbToApp, eventDbToApp, productDbToApp } from "../utils/supabaseDbToApp";
import {
  INIT_CLIENTS,
  INIT_EVENTS,
  DEF_SCHED,
  DEF_THEME,
  DEFAULT_EQUIP,
  DEFAULT_TARIF,
} from "../constants";
import { INIT_PRODUCTS } from "../constants/products";
import { migrateProduct } from "../utils/productMigration";
import { migrateAllClients } from "../utils/clientProductMigration";
import {
  mergePersistedClientsWithInit,
  mergePersistedEventsWithInit,
  mergePersistedProductsWithInit,
} from "../utils/mergePersistedWithInit";
import { buildDefaultCoachCharges } from "../constants/coachCharges";
import { logger } from "../utils/logger";
import { PERSIST_SCHEMA_VERSION } from "../constants/persistSchema";
import { normalizeLegacyNoShowClients, normalizeLegacyNoShowEvents } from "../utils/normalizeLegacyNoShow";
import { createBillingSlice } from "./slices/billingSlice";
import { createSettingsSlice } from "./slices/settingsSlice";
import { createEventsSlice } from "./slices/eventsSlice";
import { createClientsSlice } from "./slices/clientsSlice";
import { createClientNotesSlice } from "./slices/clientNotesSlice";
import { createUiSlice } from "./slices/uiSlice";

const MIGRATED_INIT_PRODUCTS = INIT_PRODUCTS.map(migrateProduct);
const INITIAL_CLIENTS = migrateAllClients(INIT_CLIENTS, MIGRATED_INIT_PRODUCTS, DEFAULT_TARIF);

let persistTimer = null;
const PERSIST_DEBOUNCE_MS = 350;

let entityPushTimer = null;
const ENTITY_PUSH_DEBOUNCE_MS = 450;

function scheduleEntityPushToSupabase(get) {
  const s = get();
  if (s.booting || !isSupabaseReady(s.coachId)) return;
  clearTimeout(entityPushTimer);
  entityPushTimer = setTimeout(() => {
    entityPushTimer = null;
    void pushNormalizedEntitiesFromStore(get);
  }, ENTITY_PUSH_DEBOUNCE_MS);
}

async function flushPersistInternal(get) {
  const s = get();
  if (s.booting) return;
  const rd = s._realData;
  const remoteSb = isSupabaseConfigured() && s.coachId && s.coachId !== "local-coach";

  const prefsPayload = {
    _schemaVersion: PERSIST_SCHEMA_VERSION,
    view: s.view,
    defaultSessionDuration: s.defaultSessionDuration,
    billingMode: s.billingMode,
    coachSchedule: s.coachSchedule,
    coachName: s.coachName,
    brandName: s.brandName,
    logoUrl: s.logoUrl,
    theme: s.theme,
    equipment: s.equipment,
    coachEmail: s.coachEmail,
    coachPhone: s.coachPhone,
    coachProfile: s.coachProfile,
    sessionTarif: s.sessionTarif,
    caObjectif: s.caObjectif,
    revenueGoal: s.revenueGoal,
    isSAP: s.isSAP,
    devise: s.devise,
    lastPaymentMethod: s.lastPaymentMethod,
    onboarded: s.onboarded,
    splashSeen: s.splashSeen,
    plan: s.plan,
    trialEnd: s.trialEnd,
    coachCharges: s.coachCharges,
    demoMode: s.demoMode,
  };

  if (remoteSb) {
    await saveData(prefsPayload, s.coachId);
    return;
  }

  await saveData(
    {
      ...prefsPayload,
      clients: !s.demoMode && rd ? rd.clients : s.clients,
      events: !s.demoMode && rd ? rd.events : s.events,
      products: s.products,
    },
    s.coachId,
  );
}

function schedulePersist(get) {
  clearTimeout(persistTimer);
  persistTimer = setTimeout(() => {
    persistTimer = null;
    void flushPersistInternal(get);
  }, PERSIST_DEBOUNCE_MS);
}

const useAppStore = create((set, get) => ({
  coachId: "local-coach",

  ...createBillingSlice(set, get),
  ...createSettingsSlice(set, get),
  ...createEventsSlice(set, get),
  ...createClientsSlice(set, get),
  ...createClientNotesSlice(set, get),
  ...createUiSlice(set, get),

  clients: INITIAL_CLIENTS,
  selectedClient: null,

  hydrate: async (remoteCoachId) => {
    const remote = isSupabaseConfigured() && remoteCoachId;
    let data;
    let bundle = null;
    let hasDbData = false;
    let dbClients = [];
    let dbEvents = [];
    let dbProducts = [];

    if (remote) {
      set({ coachId: remoteCoachId });
      bundle = await loadRemoteCoachState(remoteCoachId);
      data = bundle?.appData ?? null;
      if (data && bundle?.settings) {
        data = mergeSettingsJsonIntoPayload(bundle.settings, data);
      }
      try {
        const h = await supabasePersistence.hydrateAll(remoteCoachId);
        dbClients = (h.clients || []).map(clientDbToApp).filter(Boolean);
        dbEvents = (h.events || []).map(eventDbToApp).filter(Boolean);
        dbProducts = (h.products || []).map((r) => migrateProduct(productDbToApp(r))).filter(Boolean);
        hasDbData = dbClients.length + dbEvents.length + dbProducts.length > 0;
      } catch (e) {
        logger.warn("[AthleOS] hydrateAll", { detail: e?.message || e });
      }

      const blobHasEntities =
        data &&
        ((Array.isArray(data.clients) && data.clients.length > 0) ||
          (Array.isArray(data.events) && data.events.length > 0) ||
          (Array.isArray(data.products) && data.products.length > 0));
      if (!hasDbData && !blobHasEntities) {
        const seeded = await seedDemoDataIfEmpty(remoteCoachId);
        if (seeded) {
          try {
            const h2 = await supabasePersistence.hydrateAll(remoteCoachId);
            dbClients = (h2.clients || []).map(clientDbToApp).filter(Boolean);
            dbEvents = (h2.events || []).map(eventDbToApp).filter(Boolean);
            dbProducts = (h2.products || []).map((r) => migrateProduct(productDbToApp(r))).filter(Boolean);
            hasDbData = dbClients.length + dbEvents.length + dbProducts.length > 0;
          } catch (e2) {
            logger.warn("[AthleOS] hydrateAll post-seed", { detail: e2?.message || e2 });
          }
        }
      }
    } else {
      data = await loadData();
    }

    if (!data && remote && !hasDbData) {
      const baseEmpty = {
        sessionTarif: DEFAULT_TARIF,
        caObjectif: 5000,
        revenueGoal: { mode: "fixed", amount: 5000 },
        isSAP: false,
        devise: "EUR",
        lastPaymentMethod: "virement",
      };
      const mergedEmpty = mergeSettingsJsonIntoPayload(bundle?.settings || {}, baseEmpty);
      set({
        clients: migrateAllClients([], INIT_PRODUCTS, mergedEmpty.sessionTarif ?? DEFAULT_TARIF),
        events: [],
        products: INIT_PRODUCTS.map(migrateProduct),
        view: "dashboard",
        defaultSessionDuration: 60,
        billingMode: "session",
        coachSchedule: DEF_SCHED,
        coachName: "",
        brandName: "",
        logoUrl: "",
        theme: DEF_THEME,
        equipment: { ...DEFAULT_EQUIP },
        coachEmail: "",
        coachPhone: "",
        coachProfile: "",
        sessionTarif: mergedEmpty.sessionTarif ?? DEFAULT_TARIF,
        caObjectif: mergedEmpty.caObjectif ?? 5000,
        isSAP: mergedEmpty.isSAP ?? false,
        devise: mergedEmpty.devise ?? "EUR",
        lastPaymentMethod: mergedEmpty.lastPaymentMethod ?? "virement",
        onboarded: true,
        splashSeen: false,
        plan: "free",
        trialEnd: null,
        demoMode: false,
        _realData: null,
        selectedClient: null,
        coachCharges: buildDefaultCoachCharges(),
      });
      set({ loggedIn: true });
      set({ booting: false });
      return;
    }

    if (!data && !remote) {
      set({ coachCharges: buildDefaultCoachCharges() });
    }

    if (hasDbData) {
      const mergedPrefs = mergeSettingsJsonIntoPayload(bundle?.settings || {}, {
        sessionTarif: DEFAULT_TARIF,
        caObjectif: 5000,
        revenueGoal: { mode: "fixed", amount: 5000 },
        isSAP: false,
        devise: "EUR",
        lastPaymentMethod: "virement",
        ...(data && typeof data === "object" ? data : {}),
      });

      const rawCl = dbClients;
      const cl = rawCl.map((c) => {
        if (c.invoiceStatus && !c.billingPeriods) {
          const billingPeriods = Object.entries(c.invoiceStatus)
            .filter(([, v]) => v === "invoiced")
            .map(([key]) => {
              const [y, m] = key.split("-").map(Number);
              const last = new Date(y, m, 0).getDate();
              return {
                from: `${y}-${String(m).padStart(2, "0")}-01`,
                to: `${y}-${String(m).padStart(2, "0")}-${String(last).padStart(2, "0")}`,
                status: "invoiced",
                declaredAt: new Date().toISOString(),
              };
            });
          const { invoiceStatus: _, ...rest } = c;
          return { ...rest, billingPeriods };
        }
        return c;
      });
      const cl2 = normalizeLegacyNoShowClients(cl);
      const migratedProducts = dbProducts;
      const sessionTarif = mergedPrefs.sessionTarif ?? DEFAULT_TARIF;
      const clientsWithProducts = migrateAllClients(cl2, migratedProducts, sessionTarif);
      const migratedEvents = normalizeLegacyNoShowEvents(dbEvents);
      let plan = mergedPrefs.plan || "free";
      let trialEnd = mergedPrefs.trialEnd || null;
      if (plan === "trial" && trialEnd && new Date(trialEnd) < new Date()) {
        plan = "free";
        trialEnd = null;
      }
      set({
        clients: clientsWithProducts,
        events: migratedEvents,
        products: migratedProducts,
        view: mergedPrefs.view || "dashboard",
        defaultSessionDuration: mergedPrefs.defaultSessionDuration ?? 60,
        billingMode: mergedPrefs.billingMode ?? "session",
        coachSchedule: mergedPrefs.coachSchedule || DEF_SCHED,
        coachName: mergedPrefs.coachName || "",
        brandName: mergedPrefs.brandName || "",
        logoUrl: mergedPrefs.logoUrl || "",
        theme: mergedPrefs.theme || DEF_THEME,
        equipment: { ...DEFAULT_EQUIP, ...(mergedPrefs.equipment || {}) },
        coachEmail: mergedPrefs.coachEmail || "",
        coachPhone: mergedPrefs.coachPhone || "",
        coachProfile: mergedPrefs.coachProfile || "",
        sessionTarif: mergedPrefs.sessionTarif || DEFAULT_TARIF,
        caObjectif: mergedPrefs.caObjectif || 5000,
        revenueGoal: mergedPrefs.revenueGoal ?? { mode: "fixed", amount: 5000 },
        isSAP: mergedPrefs.isSAP ?? false,
        devise: mergedPrefs.devise || "EUR",
        lastPaymentMethod: mergedPrefs.lastPaymentMethod || "virement",
        onboarded: true,
        splashSeen: !!mergedPrefs.splashSeen,
        plan,
        trialEnd,
        demoMode: mergedPrefs.demoMode ?? false,
        _realData: null,
        coachCharges:
          Array.isArray(mergedPrefs.coachCharges) && mergedPrefs.coachCharges.length > 0
            ? mergedPrefs.coachCharges
            : buildDefaultCoachCharges(),
      });
      get().extendRecurringSeries();
      set({ loggedIn: true });
      set({ booting: false });
      return;
    }

    if (data) {
      if (!remote && (data._schemaVersion == null || data._schemaVersion < PERSIST_SCHEMA_VERSION)) {
        data = {
          ...data,
          clients: [],
          events: [],
          products: [],
          _schemaVersion: PERSIST_SCHEMA_VERSION,
        };
      }
      let rawCl = Array.isArray(data.clients) && data.clients.length > 0 ? data.clients : INIT_CLIENTS;
      if (!remote) {
        rawCl = mergePersistedClientsWithInit(rawCl, INIT_CLIENTS);
      }
      const cl = rawCl.map((c) => {
        if (c.invoiceStatus && !c.billingPeriods) {
          const billingPeriods = Object.entries(c.invoiceStatus)
            .filter(([, v]) => v === "invoiced")
            .map(([key]) => {
              const [y, m] = key.split("-").map(Number);
              const last = new Date(y, m, 0).getDate();
              return {
                from: `${y}-${String(m).padStart(2, "0")}-01`,
                to: `${y}-${String(m).padStart(2, "0")}-${String(last).padStart(2, "0")}`,
                status: "invoiced",
                declaredAt: new Date().toISOString(),
              };
            });
          const { invoiceStatus: _, ...rest } = c;
          return { ...rest, billingPeriods };
        }
        return c;
      });
      const cl2 = normalizeLegacyNoShowClients(cl);
      let rawProducts =
        Array.isArray(data.products) && data.products.length > 0 ? data.products : INIT_PRODUCTS;
      if (!remote) {
        rawProducts = mergePersistedProductsWithInit(rawProducts, INIT_PRODUCTS);
      }
      const migratedProducts = rawProducts.map(migrateProduct);
      let rawEv = Array.isArray(data.events) && data.events.length > 0 ? data.events : INIT_EVENTS;
      if (!remote) {
        rawEv = mergePersistedEventsWithInit(rawEv, INIT_EVENTS);
      }
      const sessionTarif = data.sessionTarif ?? DEFAULT_TARIF;
      const clientsWithProducts = migrateAllClients(cl2, migratedProducts, sessionTarif);
      const migratedEvents = normalizeLegacyNoShowEvents(
        rawEv.map((e) => {
          if (e.date) return e;
          const parts = e.day?.match(/(\d+)/);
          const dayNum = parts ? parseInt(parts[1], 10) : null;
          if (!dayNum) return e;
          const now = new Date();
          const date = new Date(now.getFullYear(), now.getMonth(), dayNum);
          const iso = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
          return {
            ...e,
            date: iso,
            duration: e.duration ?? 60,
            type: e.type ?? "recurring",
            recurringGroupId: e.recurringGroupId ?? null,
            tarif: e.tarif ?? sessionTarif ?? DEFAULT_TARIF,
          };
        })
      );
      let plan = data.plan || "free";
      let trialEnd = data.trialEnd || null;
      if (plan === "trial" && trialEnd && new Date(trialEnd) < new Date()) {
        plan = "free";
        trialEnd = null;
      }
      set({
        clients: clientsWithProducts,
        events: migratedEvents,
        products: migratedProducts,
        view: data.view || "dashboard",
        defaultSessionDuration: data.defaultSessionDuration ?? 60,
        billingMode: data.billingMode ?? "session",
        coachSchedule: data.coachSchedule || DEF_SCHED,
        coachName: data.coachName || "",
        brandName: data.brandName || "",
        logoUrl: data.logoUrl || "",
        theme: data.theme || DEF_THEME,
        equipment: { ...DEFAULT_EQUIP, ...(data.equipment || {}) },
        coachEmail: data.coachEmail || "",
        coachPhone: data.coachPhone || "",
        sessionTarif: data.sessionTarif || DEFAULT_TARIF,
        caObjectif: data.caObjectif || 5000,
        revenueGoal: data.revenueGoal ?? { mode: "fixed", amount: 5000 },
        isSAP: data.isSAP ?? false,
        devise: data.devise || "EUR",
        lastPaymentMethod: data.lastPaymentMethod || "virement",
        onboarded: true,
        splashSeen: !!data.splashSeen,
        plan,
        trialEnd,
        demoMode: data.demoMode ?? (remote ? false : true),
        _realData: null,
        coachCharges:
          Array.isArray(data.coachCharges) && data.coachCharges.length > 0
            ? data.coachCharges
            : buildDefaultCoachCharges(),
      });
      get().extendRecurringSeries();
    }
    set({ loggedIn: true });
    set({ booting: false });
  },

  persist: () => schedulePersist(get),

  flushPersist: async () => {
    clearTimeout(persistTimer);
    persistTimer = null;
    await flushPersistInternal(get);
  },
}));

const PERSIST_KEYS = [
  "clients",
  "events",
  "products",
  "view",
  "defaultSessionDuration",
  "billingMode",
  "coachSchedule",
  "coachName",
  "brandName",
  "logoUrl",
  "theme",
  "equipment",
  "coachEmail",
  "coachPhone",
  "coachProfile",
  "sessionTarif",
  "caObjectif",
  "revenueGoal",
  "isSAP",
  "devise",
  "lastPaymentMethod",
  "coachCharges",
  "onboarded",
  "splashSeen",
  "plan",
  "trialEnd",
];

useAppStore.subscribe((state, prevState) => {
  if (state.booting) return;
  const changed = PERSIST_KEYS.some((k) => state[k] !== prevState[k]);
  if (changed) schedulePersist(() => useAppStore.getState());
});

useAppStore.subscribe((state, prevState) => {
  if (state.booting) return;
  if (["clients", "events", "products"].some((k) => state[k] !== prevState[k])) {
    scheduleEntityPushToSupabase(() => useAppStore.getState());
  }
});

export default useAppStore;
